/**
 * Section Diff Modal
 *
 * XL modal for viewing a single section's diff comparison with approve/reject actions.
 * Used when user clicks "Diff" button on an affected section during auto-approve mode.
 */

import React, { useCallback } from 'react';
import type { NoteSection } from '@features/notes';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@store/hooks';

import { selectAssistantStatus } from '../store/selectors';

import { SectionComparison } from './SectionComparison';

export interface SectionDiffModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Section data
   */
  section: NoteSection;

  /**
   * AI-generated content (from pending suggestion or edit)
   */
  newContent: string;

  /**
   * Previous content before AI edit (for diff comparison)
   */
  previousContent: string;

  /**
   * Callback when user approves the changes
   */
  onApprove: () => void;

  /**
   * Callback when user rejects the changes
   */
  onReject: () => void;
}

/**
 * SectionDiffModal - XL dialog showing section comparison with approve/reject actions
 */
export const SectionDiffModal: React.FC<SectionDiffModalProps> = ({
  open,
  onClose,
  section,
  newContent,
  previousContent,
  onApprove,
  onReject,
}) => {
  const assistantStatus = useAppSelector(selectAssistantStatus);
  const isProcessing = assistantStatus === 'thinking';

  const handleApprove = useCallback(() => {
    onApprove();
    onClose();
  }, [onApprove, onClose]);

  const handleReject = useCallback(() => {
    onReject();
    onClose();
  }, [onReject, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
     maxWidth={"xl"} slotProps={{ paper: {
        sx: {
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      } }}>
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pb: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 1 }}>
            Review Changes: {section.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare the original section with AI-generated changes
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isProcessing}
          aria-label="Close dialog"
          sx={{ mt: -1, mr: -1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content - Section Comparison */}
      <DialogContent
        dividers
        sx={{
          p: 0,
          overflow: 'auto',
          flex: 1,
        }}
      >
        <SectionComparison
          sectionName={section.name}
          sectionId={section.section_id}
          originalContent={previousContent}
          newContent={newContent}
          styleIdPrefix="section-diff-modal"
        />
      </DialogContent>

      {/* Footer - Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<HighlightOffIcon />}
          onClick={handleReject}
          disabled={isProcessing}
          sx={{ minWidth: 120 }}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleApprove}
          disabled={isProcessing}
          sx={{ minWidth: 120 }}
        >
          {isProcessing ? 'Applying...' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
